import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as action from './common.actions';
import { catchError, forkJoin, map, mergeMap, of, tap } from 'rxjs';
import { CommonService } from '../../shared/services/common.service';
import { MessageService } from 'primeng/api';

@Injectable()
export class CommonEffects {

  private actions$ = inject(Actions);
  private commonService$ = inject(CommonService);
  private messageService = inject(MessageService);

  findRoad$ = createEffect(() => this.actions$.pipe(
    ofType(action.findRoads),
    mergeMap(payload => this.commonService$
      .findRoads(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findRoadsSuccess({ roads: payload })),
        catchError(error => of(action.findRoadsError({ error })))
      )
    )
  ))

  createRoad$ = createEffect(() => this.actions$.pipe(
    ofType(action.createRoad),
    mergeMap(payload => this.commonService$
      .createRoad(payload.road).pipe(
        map(road => action.createRoadSuccess({ road })),
        catchError(error => of(action.createRoadError({ error })))
      )
    )
  ))

  updateRoad$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateRoad),
    mergeMap(payload => this.commonService$
      .updateRoad(payload.id, payload.road).pipe(
        map(road => action.updateRoadSuccess({ road })),
        catchError(error => of(action.updateRoadError({ error })))
      )
    )
  ))

  deleteRoad$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteRoad),
    mergeMap(payload => this.commonService$
      .deleteRoad(payload.id).pipe(
        map(() => action.deleteRoadSuccess({ id: payload.id })),
        catchError(error => of(action.deleteRoadError({ error })))
      )
    )
  ))

  // Bulk Delete Road
  bulkDeleteRoads$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteRoads),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteRoad(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteRoadsError({ error: { failedIds } });
          }
          return action.bulkDeleteRoadsSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteRoadsError({ error })))
      );
    })
  ))

  // Success message handlers
  showSuccessMessage$ = createEffect(() => this.actions$.pipe(
    ofType(
      action.createRoadSuccess, action.updateRoadSuccess, action.deleteRoadSuccess,
      action.createBoundSuccess, action.updateBoundSuccess, action.deleteBoundSuccess,
      action.createZoneSuccess, action.updateZoneSuccess, action.deleteZoneSuccess,
      action.createAssetCodeSuccess, action.updateAssetCodeSuccess, action.deleteAssetCodeSuccess,
      action.createAssetCodesGroupDistinctSuccess, action.updateAssetCodesGroupDistinctSuccess, action.deleteAssetCodesGroupDistinctSuccess,
      action.createLccaSuccess, action.updateLccaSuccess, action.deleteLccaSuccess,
      action.createLccaPeriodicalSuccess, action.updateLccaPeriodicalSuccess, action.deleteLccaPeriodicalSuccess,
      action.createLccaTriggerSuccess, action.updateLccaTriggerSuccess, action.deleteLccaTriggerSuccess,
      action.createLccaTypeSuccess, action.updateLccaTypeSuccess, action.deleteLccaTypeSuccess,
      action.createLccaUnitSuccess, action.updateLccaUnitSuccess, action.deleteLccaUnitSuccess,
      action.bulkDeleteRoadsSuccess, action.bulkDeleteBoundsSuccess, action.bulkDeleteZonesSuccess,
      action.bulkDeleteAssetCodesSuccess, action.bulkDeleteAssetCodesGroupDistinctSuccess,
      action.bulkDeleteLccasSuccess,
      action.bulkDeleteLccaPeriodicalsSuccess, action.bulkDeleteLccaTriggersSuccess,
      action.bulkDeleteLccaTypesSuccess, action.bulkDeleteLccaUnitsSuccess
    ),
    tap((result) => {
      const messages: Record<string, string> = {
        [action.createRoadSuccess.type]: 'Road created successfully',
        [action.updateRoadSuccess.type]: 'Road updated successfully',
        [action.deleteRoadSuccess.type]: 'Road deleted successfully',
        [action.createBoundSuccess.type]: 'Bound created successfully',
        [action.updateBoundSuccess.type]: 'Bound updated successfully',
        [action.deleteBoundSuccess.type]: 'Bound deleted successfully',
        [action.createZoneSuccess.type]: 'Zone created successfully',
        [action.updateZoneSuccess.type]: 'Zone updated successfully',
        [action.deleteZoneSuccess.type]: 'Zone deleted successfully',
        [action.createAssetCodeSuccess.type]: 'Asset code created successfully',
        [action.updateAssetCodeSuccess.type]: 'Asset code updated successfully',
        [action.deleteAssetCodeSuccess.type]: 'Asset code deleted successfully',
        [action.createAssetCodesGroupDistinctSuccess.type]: 'Asset codes group distinct created successfully',
        [action.updateAssetCodesGroupDistinctSuccess.type]: 'Asset codes group distinct updated successfully',
        [action.deleteAssetCodesGroupDistinctSuccess.type]: 'Asset codes group distinct deleted successfully',
        [action.createLccaSuccess.type]: 'LCCA created successfully',
        [action.updateLccaSuccess.type]: 'LCCA updated successfully',
        [action.deleteLccaSuccess.type]: 'LCCA deleted successfully',
        [action.createLccaPeriodicalSuccess.type]: 'LCCA periodical created successfully',
        [action.updateLccaPeriodicalSuccess.type]: 'LCCA periodical updated successfully',
        [action.deleteLccaPeriodicalSuccess.type]: 'LCCA periodical deleted successfully',
        [action.createLccaTriggerSuccess.type]: 'LCCA trigger created successfully',
        [action.updateLccaTriggerSuccess.type]: 'LCCA trigger updated successfully',
        [action.deleteLccaTriggerSuccess.type]: 'LCCA trigger deleted successfully',
        [action.createLccaTypeSuccess.type]: 'LCCA type created successfully',
        [action.updateLccaTypeSuccess.type]: 'LCCA type updated successfully',
        [action.deleteLccaTypeSuccess.type]: 'LCCA type deleted successfully',
        [action.createLccaUnitSuccess.type]: 'LCCA unit created successfully',
        [action.updateLccaUnitSuccess.type]: 'LCCA unit updated successfully',
        [action.deleteLccaUnitSuccess.type]: 'LCCA unit deleted successfully',
        [action.bulkDeleteRoadsSuccess.type]: 'Roads deleted successfully',
        [action.bulkDeleteBoundsSuccess.type]: 'Bounds deleted successfully',
        [action.bulkDeleteZonesSuccess.type]: 'Zones deleted successfully',
        [action.bulkDeleteAssetCodesSuccess.type]: 'Asset codes deleted successfully',
        [action.bulkDeleteAssetCodesGroupDistinctSuccess.type]: 'Asset codes group distinct deleted successfully',
        [action.bulkDeleteLccasSuccess.type]: 'LCCAs deleted successfully',
        [action.bulkDeleteLccaPeriodicalsSuccess.type]: 'LCCA periodicals deleted successfully',
        [action.bulkDeleteLccaTriggersSuccess.type]: 'LCCA triggers deleted successfully',
        [action.bulkDeleteLccaTypesSuccess.type]: 'LCCA types deleted successfully',
        [action.bulkDeleteLccaUnitsSuccess.type]: 'LCCA units deleted successfully'
      };

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: messages[result.type] || 'Operation completed successfully'
      });
    })
  ), { dispatch: false });

  // Error message handlers
  showErrorMessage$ = createEffect(() => this.actions$.pipe(
    ofType(
      action.createRoadError, action.updateRoadError, action.deleteRoadError,
      action.createBoundError, action.updateBoundError, action.deleteBoundError,
      action.createZoneError, action.updateZoneError, action.deleteZoneError,
      action.createAssetCodeError, action.updateAssetCodeError, action.deleteAssetCodeError,
      action.createAssetCodesGroupDistinctError, action.updateAssetCodesGroupDistinctError, action.deleteAssetCodesGroupDistinctError,
      action.createLccaError, action.updateLccaError, action.deleteLccaError,
      action.createLccaPeriodicalError, action.updateLccaPeriodicalError, action.deleteLccaPeriodicalError,
      action.createLccaTriggerError, action.updateLccaTriggerError, action.deleteLccaTriggerError,
      action.createLccaTypeError, action.updateLccaTypeError, action.deleteLccaTypeError,
      action.createLccaUnitError, action.updateLccaUnitError, action.deleteLccaUnitError,
      action.bulkDeleteRoadsError, action.bulkDeleteBoundsError, action.bulkDeleteZonesError,
      action.bulkDeleteAssetCodesError, action.bulkDeleteAssetCodesGroupDistinctError,
      action.bulkDeleteLccasError,
      action.bulkDeleteLccaPeriodicalsError, action.bulkDeleteLccaTriggersError,
      action.bulkDeleteLccaTypesError, action.bulkDeleteLccaUnitsError
    ),
    tap((result) => {
      const messages: Record<string, string> = {
        [action.createRoadError.type]: 'Failed to create road',
        [action.updateRoadError.type]: 'Failed to update road',
        [action.deleteRoadError.type]: 'Failed to delete road',
        [action.createBoundError.type]: 'Failed to create bound',
        [action.updateBoundError.type]: 'Failed to update bound',
        [action.deleteBoundError.type]: 'Failed to delete bound',
        [action.createZoneError.type]: 'Failed to create zone',
        [action.updateZoneError.type]: 'Failed to update zone',
        [action.deleteZoneError.type]: 'Failed to delete zone',
        [action.createAssetCodeError.type]: 'Failed to create asset code',
        [action.updateAssetCodeError.type]: 'Failed to update asset code',
        [action.deleteAssetCodeError.type]: 'Failed to delete asset code',
        [action.createAssetCodesGroupDistinctError.type]: 'Failed to create asset codes group distinct',
        [action.updateAssetCodesGroupDistinctError.type]: 'Failed to update asset codes group distinct',
        [action.deleteAssetCodesGroupDistinctError.type]: 'Failed to delete asset codes group distinct',
        [action.createLccaError.type]: 'Failed to create LCCA',
        [action.updateLccaError.type]: 'Failed to update LCCA',
        [action.deleteLccaError.type]: 'Failed to delete LCCA',
        [action.createLccaPeriodicalError.type]: 'Failed to create LCCA periodical',
        [action.updateLccaPeriodicalError.type]: 'Failed to update LCCA periodical',
        [action.deleteLccaPeriodicalError.type]: 'Failed to delete LCCA periodical',
        [action.createLccaTriggerError.type]: 'Failed to create LCCA trigger',
        [action.updateLccaTriggerError.type]: 'Failed to update LCCA trigger',
        [action.deleteLccaTriggerError.type]: 'Failed to delete LCCA trigger',
        [action.createLccaTypeError.type]: 'Failed to create LCCA type',
        [action.updateLccaTypeError.type]: 'Failed to update LCCA type',
        [action.deleteLccaTypeError.type]: 'Failed to delete LCCA type',
        [action.createLccaUnitError.type]: 'Failed to create LCCA unit',
        [action.updateLccaUnitError.type]: 'Failed to update LCCA unit',
        [action.deleteLccaUnitError.type]: 'Failed to delete LCCA unit',
        [action.bulkDeleteRoadsError.type]: 'Failed to delete roads',
        [action.bulkDeleteBoundsError.type]: 'Failed to delete bounds',
        [action.bulkDeleteZonesError.type]: 'Failed to delete zones',
        [action.bulkDeleteAssetCodesError.type]: 'Failed to delete asset codes',
        [action.bulkDeleteAssetCodesGroupDistinctError.type]: 'Failed to delete asset codes group distinct',
        [action.bulkDeleteLccasError.type]: 'Failed to delete LCCAs',
        [action.bulkDeleteLccaPeriodicalsError.type]: 'Failed to delete LCCA periodicals',
        [action.bulkDeleteLccaTriggersError.type]: 'Failed to delete LCCA triggers',
        [action.bulkDeleteLccaTypesError.type]: 'Failed to delete LCCA types',
        [action.bulkDeleteLccaUnitsError.type]: 'Failed to delete LCCA units'
      };

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: messages[result.type] || 'Operation failed'
      });
    })
  ), { dispatch: false });

  ///// Bound /////
  findBound$ = createEffect(() => this.actions$.pipe(
    ofType(action.findBounds),
    mergeMap(payload => this.commonService$
      .findBounds(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findBoundsSuccess({ bounds: payload })),
        catchError(error => of(action.findBoundsError({ error })))
      )
    )
  ))

  createBound$ = createEffect(() => this.actions$.pipe(
    ofType(action.createBound),
    mergeMap(payload => this.commonService$
      .createBound(payload.bound).pipe(
        map(bound => action.createBoundSuccess({ bound })),
        catchError(error => of(action.createBoundError({ error })))
      )
    )
  ))

  updateBound$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateBound),
    mergeMap(payload => this.commonService$
      .updateBound(payload.id, payload.bound).pipe(
        map(bound => action.updateBoundSuccess({ bound })),
        catchError(error => of(action.updateBoundError({ error })))
      )
    )
  ))

  deleteBound$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteBound),
    mergeMap(payload => this.commonService$
      .deleteBound(payload.id).pipe(
        map(() => action.deleteBoundSuccess({ id: payload.id })),
        catchError(error => of(action.deleteBoundError({ error })))
      )
    )
  ))

  ///// Zone /////
  findZone$ = createEffect(() => this.actions$.pipe(
    ofType(action.findZones),
    mergeMap(payload => this.commonService$
      .findZones(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findZonesSuccess({ zones: payload })),
        catchError(error => of(action.findZonesError({ error })))
      )
    )
  ))

  createZone$ = createEffect(() => this.actions$.pipe(
    ofType(action.createZone),
    mergeMap(payload => this.commonService$
      .createZone(payload.zone).pipe(
        map(zone => action.createZoneSuccess({ zone })),
        catchError(error => of(action.createZoneError({ error })))
      )
    )
  ))

  updateZone$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateZone),
    mergeMap(payload => this.commonService$
      .updateZone(payload.id, payload.zone).pipe(
        map(zone => action.updateZoneSuccess({ zone })),
        catchError(error => of(action.updateZoneError({ error })))
      )
    )
  ))

  deleteZone$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteZone),
    mergeMap(payload => this.commonService$
      .deleteZone(payload.id).pipe(
        map(() => action.deleteZoneSuccess({ id: payload.id })),
        catchError(error => of(action.deleteZoneError({ error })))
      )
    )
  ))

  // Bulk Delete Bound
  bulkDeleteBounds$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteBounds),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteBound(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteBoundsError({ error: { failedIds } });
          }
          return action.bulkDeleteBoundsSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteBoundsError({ error })))
      );
    })
  ))

  // Bulk Delete Zone
  bulkDeleteZones$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteZones),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteZone(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteZonesError({ error: { failedIds } });
          }
          return action.bulkDeleteZonesSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteZonesError({ error })))
      );
    })
  ))

  ///// AssetCode /////
  findAssetCode$ = createEffect(() => this.actions$.pipe(
    ofType(action.findAssetCodes),
    mergeMap(payload => this.commonService$
      .findAssetCodes(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findAssetCodesSuccess({ assetCodes: payload })),
        catchError(error => {
          console.error('Error fetching asset codes:', error);
          console.error('Error URL:', error.url);
          console.error('Error status:', error.status);
          console.error('Error message:', error.message);
          return of(action.findAssetCodesError({ error }))
        })
      )
    )
  ))

  createAssetCode$ = createEffect(() => this.actions$.pipe(
    ofType(action.createAssetCode),
    mergeMap(payload => this.commonService$
      .createAssetCode(payload.assetCode).pipe(
        map(assetCode => action.createAssetCodeSuccess({ assetCode })),
        catchError(error => of(action.createAssetCodeError({ error })))
      )
    )
  ))

  updateAssetCode$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateAssetCode),
    mergeMap(payload => this.commonService$
      .updateAssetCode(payload.id, payload.assetCode).pipe(
        map(assetCode => action.updateAssetCodeSuccess({ assetCode })),
        catchError(error => of(action.updateAssetCodeError({ error })))
      )
    )
  ))

  deleteAssetCode$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteAssetCode),
    mergeMap(payload => this.commonService$
      .deleteAssetCode(payload.id).pipe(
        map(() => action.deleteAssetCodeSuccess({ id: payload.id })),
        catchError(error => of(action.deleteAssetCodeError({ error })))
      )
    )
  ))

  bulkDeleteAssetCodes$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteAssetCodes),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteAssetCode(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteAssetCodesError({ error: { failedIds } });
          }
          return action.bulkDeleteAssetCodesSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteAssetCodesError({ error })))
      );
    })
  ))

  ///// AssetCodesGroupDistinct /////
  findAssetCodesGroupDistinct$ = createEffect(() => this.actions$.pipe(
    ofType(action.findAssetCodesGroupDistinct),
    mergeMap(payload => this.commonService$
      .findAssetCodesGroupDistinct(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findAssetCodesGroupDistinctSuccess({ assetCodesGroupDistinct: payload })),
        catchError(error => of(action.findAssetCodesGroupDistinctError({ error })))
      )
    )
  ))

  createAssetCodesGroupDistinct$ = createEffect(() => this.actions$.pipe(
    ofType(action.createAssetCodesGroupDistinct),
    mergeMap(payload => this.commonService$
      .createAssetCodesGroupDistinct(payload.assetCodesGroupDistinct).pipe(
        map(assetCodesGroupDistinct => action.createAssetCodesGroupDistinctSuccess({ assetCodesGroupDistinct })),
        catchError(error => of(action.createAssetCodesGroupDistinctError({ error })))
      )
    )
  ))

  updateAssetCodesGroupDistinct$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateAssetCodesGroupDistinct),
    mergeMap(payload => this.commonService$
      .updateAssetCodesGroupDistinct(payload.id, payload.assetCodesGroupDistinct).pipe(
        map(assetCodesGroupDistinct => action.updateAssetCodesGroupDistinctSuccess({ assetCodesGroupDistinct })),
        catchError(error => of(action.updateAssetCodesGroupDistinctError({ error })))
      )
    )
  ))

  deleteAssetCodesGroupDistinct$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteAssetCodesGroupDistinct),
    mergeMap(payload => this.commonService$
      .deleteAssetCodesGroupDistinct(payload.id).pipe(
        map(() => action.deleteAssetCodesGroupDistinctSuccess({ id: payload.id })),
        catchError(error => of(action.deleteAssetCodesGroupDistinctError({ error })))
      )
    )
  ))

  bulkDeleteAssetCodesGroupDistinct$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteAssetCodesGroupDistinct),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteAssetCodesGroupDistinct(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteAssetCodesGroupDistinctError({ error: { failedIds } });
          }
          return action.bulkDeleteAssetCodesGroupDistinctSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteAssetCodesGroupDistinctError({ error })))
      );
    })
  ))

  ///// Lcca /////
  findLccas$ = createEffect(() => this.actions$.pipe(
    ofType(action.findLccas),
    mergeMap(payload => this.commonService$
      .findLccas(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findLccasSuccess({ lccas: payload })),
        catchError(error => of(action.findLccasError({ error })))
      )
    )
  ))

  createLcca$ = createEffect(() => this.actions$.pipe(
    ofType(action.createLcca),
    mergeMap(payload => this.commonService$
      .createLcca(payload.lcca).pipe(
        map(lcca => action.createLccaSuccess({ lcca })),
        catchError(error => of(action.createLccaError({ error })))
      )
    )
  ))

  updateLcca$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateLcca),
    mergeMap(payload => this.commonService$
      .updateLcca(payload.id, payload.lcca).pipe(
        map(lcca => action.updateLccaSuccess({ lcca })),
        catchError(error => of(action.updateLccaError({ error })))
      )
    )
  ))

  deleteLcca$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteLcca),
    mergeMap(payload => this.commonService$
      .deleteLcca(payload.id).pipe(
        map(() => action.deleteLccaSuccess({ id: payload.id })),
        catchError(error => of(action.deleteLccaError({ error })))
      )
    )
  ))

  bulkDeleteLccas$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteLccas),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteLcca(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );

      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteLccasError({ error: { failedIds } });
          }
          return action.bulkDeleteLccasSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteLccasError({ error })))
      );
    })
  ))

  ///// LccaPeriodical /////
  findLccaPeriodical$ = createEffect(() => this.actions$.pipe(
    ofType(action.findLccaPeriodicals),
    mergeMap(payload => this.commonService$
      .findLccaPeriodicals(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findLccaPeriodicalsSuccess({ lccaPeriodicals: payload })),
        catchError(error => of(action.findLccaPeriodicalsError({ error })))
      )
    )
  ))

  createLccaPeriodical$ = createEffect(() => this.actions$.pipe(
    ofType(action.createLccaPeriodical),
    mergeMap(payload => this.commonService$
      .createLccaPeriodical(payload.lccaPeriodical).pipe(
        map(lccaPeriodical => action.createLccaPeriodicalSuccess({ lccaPeriodical })),
        catchError(error => of(action.createLccaPeriodicalError({ error })))
      )
    )
  ))

  updateLccaPeriodical$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateLccaPeriodical),
    mergeMap(payload => this.commonService$
      .updateLccaPeriodical(payload.id, payload.lccaPeriodical).pipe(
        map(lccaPeriodical => action.updateLccaPeriodicalSuccess({ lccaPeriodical })),
        catchError(error => of(action.updateLccaPeriodicalError({ error })))
      )
    )
  ))

  deleteLccaPeriodical$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteLccaPeriodical),
    mergeMap(payload => this.commonService$
      .deleteLccaPeriodical(payload.id).pipe(
        map(() => action.deleteLccaPeriodicalSuccess({ id: payload.id })),
        catchError(error => of(action.deleteLccaPeriodicalError({ error })))
      )
    )
  ))

  bulkDeleteLccaPeriodicals$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteLccaPeriodicals),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteLccaPeriodical(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteLccaPeriodicalsError({ error: { failedIds } });
          }
          return action.bulkDeleteLccaPeriodicalsSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteLccaPeriodicalsError({ error })))
      );
    })
  ))

  ///// LccaTrigger /////
  findLccaTrigger$ = createEffect(() => this.actions$.pipe(
    ofType(action.findLccaTriggers),
    mergeMap(payload => this.commonService$
      .findLccaTriggers(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findLccaTriggersSuccess({ lccaTriggers: payload })),
        catchError(error => of(action.findLccaTriggersError({ error })))
      )
    )
  ))

  createLccaTrigger$ = createEffect(() => this.actions$.pipe(
    ofType(action.createLccaTrigger),
    mergeMap(payload => this.commonService$
      .createLccaTrigger(payload.lccaTrigger).pipe(
        map(lccaTrigger => action.createLccaTriggerSuccess({ lccaTrigger })),
        catchError(error => of(action.createLccaTriggerError({ error })))
      )
    )
  ))

  updateLccaTrigger$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateLccaTrigger),
    mergeMap(payload => this.commonService$
      .updateLccaTrigger(payload.id, payload.lccaTrigger).pipe(
        map(lccaTrigger => action.updateLccaTriggerSuccess({ lccaTrigger })),
        catchError(error => of(action.updateLccaTriggerError({ error })))
      )
    )
  ))

  deleteLccaTrigger$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteLccaTrigger),
    mergeMap(payload => this.commonService$
      .deleteLccaTrigger(payload.id).pipe(
        map(() => action.deleteLccaTriggerSuccess({ id: payload.id })),
        catchError(error => of(action.deleteLccaTriggerError({ error })))
      )
    )
  ))

  bulkDeleteLccaTriggers$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteLccaTriggers),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteLccaTrigger(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteLccaTriggersError({ error: { failedIds } });
          }
          return action.bulkDeleteLccaTriggersSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteLccaTriggersError({ error })))
      );
    })
  ))

  ///// LccaType /////
  findLccaType$ = createEffect(() => this.actions$.pipe(
    ofType(action.findLccaTypes),
    mergeMap(payload => this.commonService$
      .findLccaTypes(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findLccaTypesSuccess({ lccaTypes: payload })),
        catchError(error => of(action.findLccaTypesError({ error })))
      )
    )
  ))

  createLccaType$ = createEffect(() => this.actions$.pipe(
    ofType(action.createLccaType),
    mergeMap(payload => this.commonService$
      .createLccaType(payload.lccaType).pipe(
        map(lccaType => action.createLccaTypeSuccess({ lccaType })),
        catchError(error => of(action.createLccaTypeError({ error })))
      )
    )
  ))

  updateLccaType$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateLccaType),
    mergeMap(payload => this.commonService$
      .updateLccaType(payload.id, payload.lccaType).pipe(
        map(lccaType => action.updateLccaTypeSuccess({ lccaType })),
        catchError(error => of(action.updateLccaTypeError({ error })))
      )
    )
  ))

  deleteLccaType$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteLccaType),
    mergeMap(payload => this.commonService$
      .deleteLccaType(payload.id).pipe(
        map(() => action.deleteLccaTypeSuccess({ id: payload.id })),
        catchError(error => of(action.deleteLccaTypeError({ error })))
      )
    )
  ))

  bulkDeleteLccaTypes$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteLccaTypes),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteLccaType(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteLccaTypesError({ error: { failedIds } });
          }
          return action.bulkDeleteLccaTypesSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteLccaTypesError({ error })))
      );
    })
  ))

  ///// LccaUnit /////
  findLccaUnit$ = createEffect(() => this.actions$.pipe(
    ofType(action.findLccaUnits),
    mergeMap(payload => this.commonService$
      .findLccaUnits(payload.page, payload.size, payload.keywords).pipe(
        map(payload => action.findLccaUnitsSuccess({ lccaUnits: payload })),
        catchError(error => of(action.findLccaUnitsError({ error })))
      )
    )
  ))

  createLccaUnit$ = createEffect(() => this.actions$.pipe(
    ofType(action.createLccaUnit),
    mergeMap(payload => this.commonService$
      .createLccaUnit(payload.lccaUnit).pipe(
        map(lccaUnit => action.createLccaUnitSuccess({ lccaUnit })),
        catchError(error => of(action.createLccaUnitError({ error })))
      )
    )
  ))

  updateLccaUnit$ = createEffect(() => this.actions$.pipe(
    ofType(action.updateLccaUnit),
    mergeMap(payload => this.commonService$
      .updateLccaUnit(payload.id, payload.lccaUnit).pipe(
        map(lccaUnit => action.updateLccaUnitSuccess({ lccaUnit })),
        catchError(error => of(action.updateLccaUnitError({ error })))
      )
    )
  ))

  deleteLccaUnit$ = createEffect(() => this.actions$.pipe(
    ofType(action.deleteLccaUnit),
    mergeMap(payload => this.commonService$
      .deleteLccaUnit(payload.id).pipe(
        map(() => action.deleteLccaUnitSuccess({ id: payload.id })),
        catchError(error => of(action.deleteLccaUnitError({ error })))
      )
    )
  ))

  bulkDeleteLccaUnits$ = createEffect(() => this.actions$.pipe(
    ofType(action.bulkDeleteLccaUnits),
    mergeMap(payload => {
      const deleteRequests = payload.ids.map(id =>
        this.commonService$.deleteLccaUnit(id).pipe(
          catchError(error => of({ error: true, id }))
        )
      );
      return forkJoin(deleteRequests).pipe(
        map(responses => {
          const failedIds = responses.filter((r: any) => r.error).map((r: any) => r.id);
          if (failedIds.length > 0) {
            return action.bulkDeleteLccaUnitsError({ error: { failedIds } });
          }
          return action.bulkDeleteLccaUnitsSuccess({ ids: payload.ids });
        }),
        catchError(error => of(action.bulkDeleteLccaUnitsError({ error })))
      );
    })
  ))
}
